import { NotificationDTO } from '@/api/DTO/notification/notification.interface';
import {
	useGetNotificationPreviewQuery,
	useGetNotificationUnreadCountQuery,
} from '@/api/notification/notificationApiSlice';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { Badge, IconButton, Popover } from '@mui/material';
import { MouseEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
	NOTIFICATION_BADGE_MAX,
	NOTIFICATION_LIST_PATH,
	NOTIFICATION_PREVIEW_SIZE,
	notificationDetailPath,
} from './notificationHelper';
import NotificationPanel from './NotificationPanel';

/**
 * Bell button and unread badge in the top bar of every page, plus the popover they
 * open. Clicking a notification opens its detail page, which is what marks it read.
 */
export default function NotificationBell() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const isOpen = Boolean(anchorEl);

	const { data: unreadCountResponse } = useGetNotificationUnreadCountQuery(
		undefined,
		{ refetchOnMountOrArgChange: true }
	);
	const unreadCount = unreadCountResponse?.data ?? 0;

	// Only fetched while the popover is open - the badge count is its own endpoint,
	// so the list is dead weight until somebody looks at it. Sending no `sortings`
	// accepts the backend default of newest first.
	const { data: previewResponse } = useGetNotificationPreviewQuery(
		{
			pagination: { page: 1, size: NOTIFICATION_PREVIEW_SIZE },
			sortings: [],
		},
		{ skip: !isOpen, refetchOnMountOrArgChange: true }
	);

	const preview = useMemo(
		() => previewResponse?.data?.items ?? [],
		[previewResponse]
	);

	// unread sitting further back than the preview reaches — surfaced in the footer
	// so the badge count and the visible list cannot contradict each other
	const hiddenUnreadCount = useMemo(
		() =>
			Math.max(unreadCount - preview.filter((item) => !item.isRead).length, 0),
		[unreadCount, preview]
	);

	const handleToggle = (event: MouseEvent<HTMLElement>) => {
		setAnchorEl(isOpen ? null : event.currentTarget);
	};

	// The detail page is what marks it read, so the popover only navigates.
	const handleNotificationClick = (notification: NotificationDTO) => {
		setAnchorEl(null);
		navigate(notificationDetailPath(notification.id));
	};

	const handleViewAll = () => {
		setAnchorEl(null);
		navigate(NOTIFICATION_LIST_PATH);
	};

	return (
		<>
			<IconButton
				color='inherit'
				aria-label={t('notification.title')}
				onClick={handleToggle}
				sx={{ ml: 0.5 }}
			>
				<Badge
					badgeContent={unreadCount}
					max={NOTIFICATION_BADGE_MAX}
					color='error'
					overlap='circular'
					sx={{
						'& .MuiBadge-badge': {
							top: 2,
							right: 2,
							height: 18,
							minWidth: 18,
							px: 0.5,
							fontSize: 11,
							fontWeight: 600,
						},
					}}
				>
					<NotificationsNoneOutlinedIcon />
				</Badge>
			</IconButton>

			<Popover
				open={isOpen}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				slotProps={{
					paper: {
						sx: {
							mt: 1,
							borderRadius: 2,
							boxShadow: '0 8px 28px rgba(33, 37, 41, 0.16)',
							overflow: 'hidden',
						},
					},
				}}
			>
				<NotificationPanel
					notifications={preview}
					unreadCount={unreadCount}
					hiddenUnreadCount={hiddenUnreadCount}
					onNotificationClick={handleNotificationClick}
					onViewAll={handleViewAll}
				/>
			</Popover>
		</>
	);
}
